import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { getLawyerGuideCategories, getLawyerGuideItems, searchLawyerGuide } from '../../services/lawyerGuideService';
import type { LawyerGuideCategory, LawyerGuideItem } from '../../types';
import { SectionHeader, SectionSearchBar } from '../../components/ui';
import { 
  BookOpen, 
  ExternalLink, 
  Search, 
  ChevronRight, 
  Globe, 
  MapPin, 
  Building2, 
  Scale, 
  FileText, 
  Users,
  Calendar,
  CreditCard,
  Shield,
  Gavel,
  Briefcase,
  Phone,
  X
} from 'lucide-react';

// Icon mapping for categories
const getCategoryIcon = (iconName?: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    'globe': <Globe className="h-6 w-6" />,
    'map-pin': <MapPin className="h-6 w-6" />,
    'building': <Building2 className="h-6 w-6" />,
    'scale': <Scale className="h-6 w-6" />,
    'file-text': <FileText className="h-6 w-6" />,
    'users': <Users className="h-6 w-6" />,
    'calendar': <Calendar className="h-6 w-6" />,
    'credit-card': <CreditCard className="h-6 w-6" />,
    'shield': <Shield className="h-6 w-6" />,
    'gavel': <Gavel className="h-6 w-6" />,
    'briefcase': <Briefcase className="h-6 w-6" />,
    'book-open': <BookOpen className="h-6 w-6" />
  };
  
  return iconMap[iconName || 'book-open'] || <BookOpen className="h-6 w-6" />;
};

export default function LawyerGuide() {
  const { strings, language, isRTL } = useLocalization();
  
  const [categories, setCategories] = useState<LawyerGuideCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<LawyerGuideCategory | null>(null);
  const [items, setItems] = useState<LawyerGuideItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load categories on component mount
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fetchedCategories = await getLawyerGuideCategories();
      setCategories(fetchedCategories);
      
      // Auto-select first category if available
      if (fetchedCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(fetchedCategories[0]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  // Load items for selected category
  const loadItems = useCallback(async (categoryId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const fetchedItems = await getLawyerGuideItems(categoryId);
      setItems(fetchedItems);
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle category selection
  const handleCategorySelect = (category: LawyerGuideCategory) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when selecting category
    loadItems(category.id);
  };

  // Handle item click based on action type
  const handleItemClick = (item: LawyerGuideItem) => {
    if (!item.data) return;

    switch (item.action) {
      case 'open_url':
        window.open(item.data, '_blank', 'noopener,noreferrer');
        break;
      case 'call':
        // For mobile app, this would trigger a phone call
        if (navigator.userAgent.match(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)) {
          window.location.href = `tel:${item.data}`;
        } else {
          // For web, show the phone number
          alert(`Phone: ${item.data}`);
        }
        break;
      case 'navigate':
        // Parse the data string: "address;lat;lng"
        const dataParts = item.data.split(';');
        if (dataParts.length === 3) {
          const [, lat, lng] = dataParts;
          // Open Google Maps for navigation with coordinates
          window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank', 'noopener,noreferrer');
        } else {
          // Fallback to address search if format is incorrect
          window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.data)}`, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'none':
        // No action required - just display the information
        console.log('Information item clicked:', item.nameEn, item.data);
        break;
      default:
        console.warn('Unknown action type:', item.action);
    }
  };

  // Handle search
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      // If search is empty, show selected category items
      if (selectedCategory) {
        loadItems(selectedCategory.id);
      }
      return;
    }

    try {
      setError(null);
      
      const searchResults = await searchLawyerGuide(query);
      
      // If we have search results, show them
      if (searchResults.items.length > 0) {
        setItems(searchResults.items);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Error searching:', err);
      setError('Search failed');
    }
  };

  const handleRefresh = () => {
    loadCategories();
  };

  const handleAddGuide = () => {
    // TODO: Add guide functionality
    console.log('Add guide clicked');
  };


  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Load items when category is selected
  useEffect(() => {
    if (selectedCategory && !searchQuery) {
      loadItems(selectedCategory.id);
    }
  }, [selectedCategory, loadItems, searchQuery]);

  if (loading && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{strings.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.lawyerGuide?.title || 'Lawyer Guide'}
          description={strings.lawyerGuide?.subtitle || 'Quick access to essential legal resources and tools'}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: loading,
            loading: loading
          }}
          addButton={{
            label: language === 'ar' ? 'إضافة دليل' : 'Add Guide',
            onClick: handleAddGuide
          }}
        >
          {/* Search Bar */}
          <div className="mb-6">
            <div className="max-w-md">
              <SectionSearchBar
                searchQuery={searchQuery}
                onSearchChange={handleSearch}
                onClearSearch={() => handleSearch('')}
                placeholder={strings.lawyerGuide?.searchPlaceholder || 'Search categories and resources...'}
                language={language}
                isRTL={isRTL}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}
        </SectionHeader>

        {/* Main Content - Two Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                  {strings.lawyerGuide?.categories || 'Categories'}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} available
                </p>
              </div>
              
              <div className="p-4">
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <BookOpen className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      No categories available
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {categories.map((category) => {
                      const isSelected = selectedCategory?.id === category.id;
                      
                      return (
                        <div
                          key={category.id}
                          className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                            isSelected 
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm' 
                              : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                          }`}
                          onClick={() => handleCategorySelect(category)}
                        >
                          <div className="flex items-center space-x-3">
                            <div 
                              className="flex-shrink-0 p-2 rounded-lg"
                              style={{ backgroundColor: category.color + '20' }}
                            >
                              <div style={{ color: category.color }}>
                                {getCategoryIcon(category.iconName)}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                                {language === 'ar' ? category.nameAr : category.nameEn}
                              </h3>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {language === 'ar' ? category.descriptionAr : category.descriptionEn}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {/* Push to Firestore Button - Commented out for production */}
                              {/* <button
                                onClick={(e) => handlePushToFirestore(category, e)}
                                disabled={pushingCategories.has(category.id) || pushedCategories.has(category.id)}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  pushedCategories.has(category.id)
                                    ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                                    : pushingCategories.has(category.id)
                                    ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                                    : 'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-500'
                                }`}
                                title={pushedCategories.has(category.id) ? 'Pushed to Firestore' : 'Push to Firestore'}
                              >
                                {pushedCategories.has(category.id) ? (
                                  <Check className="w-4 h-4" />
                                ) : pushingCategories.has(category.id) ? (
                                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                              </button> */}
                              {isSelected && (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Items */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                      {searchQuery ? 'Search Results' : (strings.lawyerGuide?.resources || 'Resources')}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      {searchQuery 
                        ? `${items.length} result${items.length !== 1 ? 's' : ''} found`
                        : selectedCategory 
                          ? `${items.length} resource${items.length !== 1 ? 's' : ''} in ${language === 'ar' ? selectedCategory.nameAr : selectedCategory.nameEn}`
                          : 'Select a category to view resources'
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                {!selectedCategory && !searchQuery ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <BookOpen className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      Select a category to view resources
                    </p>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Search className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      {searchQuery ? 'No results found' : 'No resources available'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="p-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-all duration-200 group"
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-800 dark:text-white text-sm mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {language === 'ar' ? item.nameAr : item.nameEn}
                            </h3>
                            {(item.descriptionEn || item.descriptionAr) && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 line-clamp-2">
                                {language === 'ar' ? item.descriptionAr : item.descriptionEn}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 text-xs text-slate-400 dark:text-slate-500">
                              {item.action === 'navigate' ? (
                                <>
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate">
                                    {item.data.split(';')[0] || item.data}
                                  </span>
                                </>
                              ) : item.action === 'call' ? (
                                <>
                                  <Phone className="h-3 w-3" />
                                  <span className="truncate">{item.data}</span>
                                </>
                              ) : (
                                <>
                                  <Globe className="h-3 w-3" />
                                  <span className="truncate">{item.data}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-primary dark:group-hover:text-blue-400 transition-colors" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
